/**
 * @file: LeadActivity.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * A lead timeline entry. The server stores a typed `LeadActivityData` payload
 * (discriminated union) and the read API returns it parsed as `data`; the
 * client renders each entry in Chinese from that structure — never from a
 * server string. `notes` is a separate optional user remark.
 */

import { LeadMediaAttachment } from './attachment';
import { LeadIntention, LeadSource, LeadStatus, LeadVisibility } from './Lead';
import { LeadItemDecision } from './LeadItem';

export type LeadConversionEvidence =
  | { kind: 'arrived'; orderNo: string | null }
  | { kind: 'coupon_redeemed'; couponId: string };

export type LeadActivityKind = 'manual' | 'system';

export type LeadActivityChannel =
  | 'phone'
  | 'sms'
  | 'wechat'
  | 'in_person'
  | 'note'
  | 'system';

export type LeadActivityOutcome =
  | 'reached_booked'
  | 'reached_declined'
  | 'considering'
  | 'postponed'
  | 'callback_requested'
  | 'no_answer'
  | 'call_rejected'
  | 'unreachable'
  | 'wrong_number'
  | 'no_reply'
  | 'send_failed'
  | 'other';

export const LeadActivityChannelLabel: Record<LeadActivityChannel, string> = {
  phone: '电话',
  sms: '短信',
  wechat: '微信',
  in_person: '到店',
  note: '备注',
  system: '系统',
};

export const LeadActivityOutcomeLabel: Record<LeadActivityOutcome, string> = {
  reached_booked: '已预约',
  reached_declined: '已婉拒',
  considering: '考虑中',
  postponed: '已改期',
  callback_requested: '约定回电',
  no_answer: '未接听',
  call_rejected: '已拒接',
  unreachable: '无法接通',
  wrong_number: '号码有误',
  no_reply: '未回复',
  send_failed: '发送失败',
  other: '其他',
};

/** A covered item, snapshotted so the entry renders after the item is gone.
 * `planDetailId` is null for catalog items (no plan detail behind them). */
export interface LeadActivityItemRef {
  planDetailId?: number | null;
  name: string;
}

export type LeadReminderAudience = 'staff' | 'customer';

export type LeadActivityData =
  | { type: 'created'; source: LeadSource; items: LeadActivityItemRef[] }
  | { type: 'items_added'; items: LeadActivityItemRef[] }
  | { type: 'item_removed'; item: LeadActivityItemRef }
  | {
      type: 'item_decision_set';
      item: LeadActivityItemRef;
      decision: LeadItemDecision | null;
    }
  | { type: 'reminder_sent'; audience: LeadReminderAudience; attempt: number }
  | { type: 'target_date_changed'; from: string; to: string }
  | {
      type: 'assignee_changed';
      fromId: number | null;
      fromName: string | null;
      toId: number;
      toName: string;
    }
  | { type: 'visibility_changed'; from: LeadVisibility; to: LeadVisibility }
  | { type: 'intention_changed'; from: LeadIntention; to: LeadIntention }
  | {
      type: 'status_changed';
      from: LeadStatus;
      to: LeadStatus;
      reason?: string | null;
    }
  | { type: 'converted'; orderNo: string; evidence?: LeadConversionEvidence }
  | { type: 'reservation_created'; reservationId: string }
  | { type: 'reservation_cancelled'; reservationId: string }
  | {
      type: 'outreach';
      channel: LeadActivityChannel;
      outcome?: LeadActivityOutcome | null;
      nextFollowUpAt?: string | null;
    };

export type LeadActivityType = LeadActivityData['type'];

/**
 * Parse a stored `meta` JSON string (e.g. lead.lastReachMeta) into a typed
 * payload. Returns null on empty/unparseable input rather than throwing.
 */
export const parseLeadActivityData = (
  raw: string | null | undefined,
): LeadActivityData | null => {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as LeadActivityData;
  } catch {
    return null;
  }
};

/**
 * The one-line "last reach" summary (e.g. 电话跟进 · 未接听) from an already-parsed
 * outreach activity. Null when it isn't an outreach.
 */
export const leadReachSummaryFromData = (
  data: LeadActivityData | null | undefined,
): string | null => {
  if (data?.type !== 'outreach') return null;
  const base = `${LeadActivityChannelLabel[data.channel]}跟进`;
  return data.outcome
    ? `${base} · ${LeadActivityOutcomeLabel[data.outcome]}`
    : base;
};

/**
 * The one-line "last reach" summary for the list, parsed from a stored outreach
 * `meta` string. Null when the meta isn't a parseable outreach (by construction
 * lastReachMeta always is).
 */
export const formatLeadReachSummary = (
  meta: string | null | undefined,
): string | null => leadReachSummaryFromData(parseLeadActivityData(meta));

/** Timeline entry as returned by the read API (`meta` parsed into `data`). */
export interface LeadActivityView {
  id: string;
  leadId: string;
  staffId: number;
  kind: LeadActivityKind;
  notes?: string | null;
  data: LeadActivityData | null;
  // Image/video attachments staff added to the activity (empty when none).
  attachments: LeadMediaAttachment[];
  createdAt: string | Date;
  createdBy: number;
  createdByName: string;
  updatedAt?: string | Date | null;
  updatedBy?: number | null;
  updatedByName?: string | null;
}
